/**
 * Keeping the dictionary on the device, and being able to ask whether it is.
 *
 * The HTTP cache already holds these files. They are served from a path with
 * the package version in it, under `immutable` and a year of `max-age`, so a
 * browser that still has one will not ask for it again and will not even
 * revalidate. What the HTTP cache will not do is answer a question. A page
 * cannot look inside it, so it cannot tell whether loading the full dictionary
 * would cost 2.6 MB or nothing at all, and a tool that cannot tell has to
 * assume the worst and ask the visitor again every time.
 *
 * The Cache API can be asked, and that is the whole reason it is here. It is a
 * store this page can look in before deciding, which is what lets the tool open
 * on the full dictionary for somebody who already has it and on the small one
 * for everybody else. That it is also more durable than the HTTP cache is a
 * second reason rather than the first: a named store is evicted under storage
 * pressure, where the HTTP cache is a least-recently-used list that a few days
 * of ordinary browsing will trim a 9 MB entry out of.
 *
 * **Nothing here is required.** The Cache API needs a secure context, and can
 * be absent, blocked or full. Every method answers as though the store were
 * empty rather than failing, so a visitor without one gets the tool exactly as
 * it behaved before this existed: the standard dictionary, and an offer.
 */

/**
 * The store's name, which is also what makes pruning safe.
 *
 * Namespaced because the site shares an origin, and everything under this name
 * is this module's to delete.
 */
const storeName = "chineseboost.dictionary";

/**
 * The browser's cache storage, where there is one.
 *
 * A function with a `try` rather than a reference, for the same reason
 * `browserStorage` in `settings-store.ts` is one: reading the property is
 * itself allowed to throw where storage has been blocked.
 */
function cacheStorage(): CacheStorage | undefined {
  try {
    return typeof caches === "undefined" ? undefined : caches;
  } catch {
    return undefined;
  }
}

/**
 * The dictionary files this device has, under one version of the package.
 */
export class DictionaryCache {
  /** Path every URL this cache owns begins with. */
  readonly #prefix: string;

  /** Opened once per page, since opening it also prunes it. */
  #opening: Promise<Cache | undefined> | undefined;

  constructor(baseUrl: string) {
    this.#prefix = `${baseUrl.replace(/\/$/u, "")}/`;
  }

  /**
   * Drop everything from a version this page is not serving.
   *
   * The published path carries the package version, so upgrading the package
   * changes every URL and leaves the old files behind under names nothing will
   * ask for again. Ten megabytes of a superseded dictionary sitting on
   * somebody's phone is not a cost this feature gets to impose, and the build
   * does the same thing to the static tree for the same reason: see
   * `removeOtherVersions` in `src/assets/dictionary-data.ts`.
   */
  async #pruneOtherVersions(store: Cache): Promise<void> {
    const stored = await store.keys();

    await Promise.all(
      stored
        .filter(
          (request) => !new URL(request.url).pathname.startsWith(this.#prefix),
        )
        .map(async (request) => store.delete(request)),
    );
  }

  async #openAndPrune(): Promise<Cache | undefined> {
    const storage = cacheStorage();
    if (storage === undefined) {
      return undefined;
    }

    try {
      const store = await storage.open(storeName);
      await this.#pruneOtherVersions(store);

      return store;
    } catch {
      return undefined;
    }
  }

  /** The store, opened at most once. */
  #store(): Promise<Cache | undefined> {
    this.#opening ??= this.#openAndPrune();

    return this.#opening;
  }

  /** Forget these files, so that wanting them again means fetching them. */
  async forget(urls: readonly string[]): Promise<void> {
    try {
      const store = await this.#store();

      await Promise.all(urls.map(async (url) => store?.delete(url)));
    } catch {
      // Nothing above this can do anything about a delete that would not
      // happen, and a file that stays behind costs disk rather than
      // correctness: the tier is re-read either way.
    }
  }

  /**
   * Whether every one of these files is already here.
   *
   * All of them or none, because a tier is three files and two of them is not
   * a tier. A partial set is treated as absent, which is right: what the
   * answer is used for is deciding whether loading is free, and it is not free
   * if one file still has to come down the wire.
   */
  async holds(urls: readonly string[]): Promise<boolean> {
    const found = await Promise.all(urls.map(async (url) => this.match(url)));

    return found.every((response) => response !== undefined);
  }

  /**
   * Store a response, or quietly do not.
   *
   * Storage can be full, and a browser can refuse it outright. Failing here
   * would fail a conversion that has everything it needs, so it does not: what
   * is lost is the free load next time.
   */
  async keep(url: string, response: Response): Promise<void> {
    try {
      const store = await this.#store();

      await store?.put(url, response);
    } catch {
      // As above. The tool has the file; only the next visit is poorer for it.
    }
  }

  /** What is stored for a URL, or nothing. */
  async match(url: string): Promise<Response | undefined> {
    try {
      const store = await this.#store();

      return await store?.match(url);
    } catch {
      return undefined;
    }
  }
}
